import { SearchFilters, PrefillFiltersProps } from '../types/travel.ts';
import {IconClose} from "./svg-icons/IconClose.tsx";
import {formatSliderPrice} from "../utils/priceFormatter.ts";

interface ActiveFiltersProps {
    filters: SearchFilters;
    cssClasses?: string;
    onFiltersChange: (filters: SearchFilters) => void;
    prefillFilters?: PrefillFiltersProps;
}

interface ActiveFilter {
    category: keyof SearchFilters;
    value: string;
}

export function ActiveFilters({ filters, onFiltersChange, cssClasses = '', prefillFilters }: ActiveFiltersProps) {

    const isPrefilled = (category: keyof SearchFilters, value: string): boolean => {
        const prefill = prefillFilters?.[category as keyof PrefillFiltersProps] as { selected?: string[] } | undefined
        if (prefill && Array.isArray(prefill.selected) && prefill.selected.includes(value)) return true
        if (category === 'difficulty' && prefillFilters?.tourencharakter?.selected) {
            return String(prefillFilters.tourencharakter.selected.label) === value
        }
        return false
    }

    // Sammle alle aktiven Filter mit Kategorie und Wert
    const getActiveFilters = (): ActiveFilter[] => {
        const activeFilters: ActiveFilter[] = [];

        Object.keys(filters).forEach((filterKey) => {
            const category = filterKey as keyof SearchFilters;
            const excludedCategories = ['start', 'end', 'destination', 'yearmonth']
            if(excludedCategories.includes(category)) {
                return;
            }

            const filterValues = filters[category];

            // Nur Array-Werte verarbeiten (start/end sind strings)
            if (Array.isArray(filterValues) && filterValues.length > 0) {
                filterValues.forEach((value) => {
                    if (!isPrefilled(category, value)) {
                        activeFilters.push({ category, value });
                    }
                });
            }
        });

        return activeFilters;
    };

    const activeFilters = getActiveFilters();

    // Entferne einen spezifischen Filter - gleiche Logik wie in FilterSidebar.tsx
    const handleRemoveFilter = (category: keyof SearchFilters, value: string) => {
        const current = (filters[category] as string[]) || [];
        const updated = current.filter(v => v !== value);

        onFiltersChange({
            ...filters,
            [category]: updated
        });
    };

    const handleRemoveAllFilters = () => {
        const protectedKeys: (keyof SearchFilters)[] = ['destination', 'end', 'start', 'yearmonth'];
        const preserved = Object.fromEntries(
            protectedKeys
                .filter(key => filters[key] !== undefined)
                .map(key => [key, filters[key]])
        ) as Partial<SearchFilters>;

        // Preserve prefilled values so they are not cleared
        Object.keys(filters).forEach((filterKey) => {
            const category = filterKey as keyof SearchFilters
            const values = filters[category]
            if (Array.isArray(values)) {
                const kept = values.filter(v => isPrefilled(category, v))
                if (kept.length > 0) (preserved as any)[category] = kept
            }
        })

        onFiltersChange(preserved as SearchFilters);
    }

    // Wenn keine Filter aktiv sind, nichts rendern
    if (activeFilters.length === 0) {
        return null;
    }

    const reisedauerMapping: { [key: string]: string } = {
        '1' : 'bis 8 Tage',
        '2' : '9-15 Tage',
        '3' : '16+ Tage',
    };


    // Formatiere Filter-Wert basierend auf Kategorie
    const formatFilterValue = (category: keyof SearchFilters, value: string): string => {
        if (category === 'duration' && reisedauerMapping[value]) {
            return reisedauerMapping[value];
        }

        if (category === 'price') {
            // Format: "500-1000" → "€500 - €1.000"
            const parts = value.split('-');
            if (parts.length === 2) {
                return `€${formatSliderPrice(parts[0])} - €${formatSliderPrice(parts[1])}`;
            }
        }

        return value;
    };

    return (
        <div className={`
            ${cssClasses}
            border-b border-theme-gray border-solid lg:border-0
            mb-5
        `}>
            <div className={`flex justify-between gap-3 items-center mb-5 lg:hidden`}>
                <h3 className={`w-auto m-0! p-0!`} >Filter (<span className={`mx-[1px]`}>{activeFilters.length}</span>)</h3>
                <button
                    className={`font-semibold text-[13px] m-0 text-theme-primary inline-flex whitespace-nowrap`}
                    onClick={() => handleRemoveAllFilters()}
                >
                    Alle löschen
                </button>
            </div>
            <div className="py-4 mb-2.5 lg:py-0 lg:mb-7 w-full overflow-x-auto overflow-y-hidden">
            <div className="inline-flex gap-2 lg:flex-wrap">
                {activeFilters.map((filter, index) => (
                    <button
                        key={`${filter.category}-${filter.value}-${index}`}
                        onClick={() => handleRemoveFilter(filter.category, filter.value)}
                        className="
                                inline-flex items-center gap-[7px]
                                px-2.5 py-1 min-h-8
                                border-2 border-solid border-theme-gray
                                rounded-[10px] text-[13px]
                                transition-all duration-100 ease-linear
                                cursor-pointer
                                whitespace-nowrap
                                hover:bg-theme-hintergrund hover:border-theme-hintergrund
                                 hover:text-shadow-[0.8px_0_#000000]
                            "
                        type="button"
                        aria-label={`Filter ${filter.value} entfernen`}
                    >
                    <span dangerouslySetInnerHTML={{ __html: formatFilterValue(filter.category, filter.value) }} />
                        <IconClose cssClasses="w-2 h-2"/>
                    </button>
                ))}
                <button
                    className={`font-semibold text-[13px] m-0 text-theme-primary whitespace-nowrap min-h-8 hidden lg:inline-flex items-center py-1 cursor-pointer`}
                    onClick={() => handleRemoveAllFilters()}
                >
                    Alle löschen
                </button>
            </div>
        </div>
        </div>
    );
}
